import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import { TrinoLinkedService } from 'app/model/linkedservice/trino.linkedservice';
import { CredentialTypeOptions, ReferenceAdapter, Credential } from 'shell-models-lib';
import { IModel } from '../../base/annotation';
import { parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel } from './azure-key-vault.util';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(TrinoLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                host: '*',
                catalog: '*',
                schema: '*',
                authenticationType: '*',
                servicePrincipalId: '*',
                servicePrincipalKey: 'secret',
                tenant: '*',
                sessionProperties: '*',
                azureCloudType: '*',
                credential: '*',
                encryptedCredential: '*'
            },
            parameters: '*'
        }
    }
})
export class TrinoLinkedServiceAdapter extends LinkedServiceAdapter<TrinoLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => TrinoLinkedService {
        return TrinoLinkedService;
    }

    protected model(): IModel {
        return TrinoLinkedService.MODEL;
    }
    protected async propertiesToResourceAsync(linkedService: TrinoLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.TrinoLinkedService;
        properties.type = Metamodel.modelFor(TrinoLinkedService).name;
        const isSP = linkedService.authenticationType === CredentialTypeOptions.ServicePrincipal;

        properties.typeProperties = {
            host: this.valueToResource(linkedService, TrinoLinkedService.HOST),
            catalog: this.valueToResource(linkedService, TrinoLinkedService.CATALOG),
            schema: this.valueToResource(linkedService, TrinoLinkedService.SCHEMA),
            authenticationType: linkedService.authenticationType,
            servicePrincipalId: isSP ? this.valueToResource(linkedService, TrinoLinkedService.CLIENT_ID) : undefined,
            servicePrincipalKey: isSP ? parameterizablePasswordOrAKVtoSecretBase(linkedService, TrinoLinkedService.CLIENT_SECRET, this.context) : undefined,
            tenant: isSP ? this.valueToResource(linkedService, TrinoLinkedService.TENANT) : undefined,
            azureCloudType: isSP ? linkedService.azureCloudType : undefined,
            credential: linkedService.authenticationType === CredentialTypeOptions.UserAssignedManagedIdentity && linkedService.credential ?
                ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference : undefined
        };

        if (!arrayIsNullOrEmpty(linkedService.sessionProperties)) {
            const spArr: string[] = [];
            linkedService.sessionProperties.forEach((pair) => {
                if (pair.value != null && pair.key != null) {
                    spArr.push(`${pair.key}:${pair.value}`);
                }
            });
            properties.typeProperties.sessionProperties = spArr.join(',');
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: TrinoLinkedService): Promise<TrinoLinkedService> {
        const properties = this.properties(linkedServiceResource) as Model.TrinoLinkedService;
        const typeProperties: Model.TrinoLinkedServiceTypeProperties = properties.typeProperties;
        linkedService.reset();

        linkedService.authenticationType = typeProperties.authenticationType;
        ValueAdapter.toModel(linkedService, TrinoLinkedService.CATALOG, typeProperties.catalog);
        ValueAdapter.toModel(linkedService, TrinoLinkedService.HOST, typeProperties.host);
        ValueAdapter.toModel(linkedService, TrinoLinkedService.SCHEMA, typeProperties.schema);
        if (linkedService.authenticationType === CredentialTypeOptions.ServicePrincipal) {
            ValueAdapter.toModel(linkedService, TrinoLinkedService.CLIENT_ID, typeProperties.servicePrincipalId);
            parameterizableSecretBaseToModel(linkedService, TrinoLinkedService.CLIENT_SECRET, typeProperties.servicePrincipalKey, this.context);
            ValueAdapter.toModel(linkedService, TrinoLinkedService.TENANT, typeProperties.tenant);
            linkedService.azureCloudType = typeProperties.azureCloudType;
        } else if (linkedService.authenticationType === CredentialTypeOptions.UserAssignedManagedIdentity) {
            linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential), typeProperties.credential, this.context);
        }

        if (typeProperties.sessionProperties) {
            linkedService.sessionProperties = [];
            const spArr = typeProperties.sessionProperties.split(',');
            spArr.forEach((pair: string) => {
                const keyValPair = pair.split(':');
                linkedService.sessionProperties.push({
                    key: keyValPair[0],
                    value: keyValPair[1]
                });
            });
        }
        return linkedService;
    }
}
